import React, { useEffect, useState } from 'react';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/DriveFileRenameOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/South';
import KeyboardArrowUpIcon from '@mui/icons-material/North';
import PlayIcon from '@mui/icons-material/PlayArrow';
import StorageIcon from '@mui/icons-material/Storage';

import { getUsersByRolesArray } from '../../api';
import { FOLDER_CONTENT_FILTER } from '../../enums/ENUMS';
import { RolesLowerCase, USER_NEW } from '../../enums';
import { SortOptions, SortType } from '../../enums/SORT';
import { ContainedButton, OutlinedButton } from '../modules/Buttons';
import Modal from '../modules/Modal';
import { PermissionSelector } from '../modules/PermissionSelector';
import styles from '../AllContent/AllContentFilters/AllContentFiltersModal.module.scss';
import localStyles from './VideofeedFiltersModal.module.scss';

export type FilterOption = { label: string; value: string };

export enum VIDEOFEED_STATUS_FILTER {
  ALL = 'all',
  UPLOADED = 'uploaded',
  DRAFTS = 'drafts',
}

type Props = {
  open: boolean;
  loading: boolean;
  sortOption: SortOptions;
  sortOrder: SortType;
  status: VIDEOFEED_STATUS_FILTER;
  contentFilter: FOLDER_CONTENT_FILTER;
  selectedOwners: FilterOption[];
  canSortByOwner: boolean;
  onClose: () => void;
  onSort: (option: SortOptions, order: SortType) => void;
  onStatusChange: (status: VIDEOFEED_STATUS_FILTER) => void;
  onContentFilterChange: (filter: FOLDER_CONTENT_FILTER) => void;
  onOwnerChange: (owners: FilterOption[]) => void;
};

const STATUS_OPTIONS = [
  { label: 'All', value: VIDEOFEED_STATUS_FILTER.ALL },
  { label: 'Uploaded', value: VIDEOFEED_STATUS_FILTER.UPLOADED },
  { label: 'Drafts', value: VIDEOFEED_STATUS_FILTER.DRAFTS },
];

const FILE_TYPE_OPTIONS = [
  { label: 'All', value: FOLDER_CONTENT_FILTER.ALL },
  { label: 'Uploaded Composer', value: FOLDER_CONTENT_FILTER.COMPOSER },
  { label: 'Timeline', value: FOLDER_CONTENT_FILTER.TIMELINE },
  { label: 'Subfolder', value: FOLDER_CONTENT_FILTER.SUBFOLDER },
];

const VideofeedFiltersModal: React.FC<Props> = ({
  open,
  loading,
  sortOption,
  sortOrder,
  status,
  contentFilter,
  selectedOwners,
  canSortByOwner,
  onClose,
  onSort,
  onStatusChange,
  onContentFilterChange,
  onOwnerChange,
}) => {
  const [ownerOptions, setOwnerOptions] = useState<FilterOption[]>([
    { label: 'All', value: 'ALL' },
  ]);
  const [ownersLoading, setOwnersLoading] = useState(false);

  useEffect(() => {
    if (!canSortByOwner) return;

    const fetchOwners = async () => {
      setOwnersLoading(true);
      try {
        const response = await getUsersByRolesArray([
          RolesLowerCase.ORGANIZATION_ADMIN,
          RolesLowerCase.ANALYST,
        ]);
        const owners = (response?.documents || []).map((entry: USER_NEW) => ({
          label: entry.user.username || entry.user.email,
          value: String(entry.user.id),
        }));
        setOwnerOptions([{ label: 'All', value: 'ALL' }, ...owners]);
      } finally {
        setOwnersLoading(false);
      }
    };

    fetchOwners();
  }, [canSortByOwner]);

  const toggleSort = (option: SortOptions, initialOrder = SortType.DESC) => {
    onSort(
      option,
      sortOption === option
        ? sortOrder === SortType.ASC
          ? SortType.DESC
          : SortType.ASC
        : initialOrder,
    );
  };

  const renderSortButton = (
    option: SortOptions,
    label: string,
    Icon: React.ElementType,
    explicitOrder?: SortType,
    initialOrder = SortType.DESC,
  ) => {
    const selected =
      sortOption === option &&
      (explicitOrder === undefined || sortOrder === explicitOrder);

    return (
      <OutlinedButton
        kind={selected ? 'purple' : 'default'}
        className={styles.optionBtn}
        onClick={() =>
          explicitOrder === undefined
            ? toggleSort(option, initialOrder)
            : onSort(option, explicitOrder)
        }
      >
        <div className={styles.leftSide}>
          <Icon
            style={{
              fontSize: '15px',
              marginLeft: '4px',
              marginRight: '8px',
            }}
          />
          {label}
        </div>
        {selected &&
          (explicitOrder === undefined ? (
            sortOrder === SortType.ASC ? (
              <KeyboardArrowDownIcon style={{ fontSize: '15px' }} />
            ) : (
              <KeyboardArrowUpIcon style={{ fontSize: '15px' }} />
            )
          ) : (
            <CheckIcon sx={{ height: '16px' }} />
          ))}
      </OutlinedButton>
    );
  };

  const selectedFilters = [
    ...(status === VIDEOFEED_STATUS_FILTER.ALL
      ? []
      : [STATUS_OPTIONS.find((option) => option.value === status)]),
    ...(contentFilter === FOLDER_CONTENT_FILTER.ALL
      ? []
      : [FILE_TYPE_OPTIONS.find((option) => option.value === contentFilter)]),
    ...(canSortByOwner && selectedOwners[0]?.value !== 'ALL'
      ? selectedOwners
      : []),
  ].filter(Boolean) as FilterOption[];

  return (
    <Modal
      overflow="hidden !important"
      open={open}
      onClose={onClose}
      loading={loading || ownersLoading}
    >
      <div className={styles.modalBody}>
        <div className={styles.modalTitle}>
          Select at least one sort or filter you want to apply
        </div>
        <div className={styles.modalSubtitle}>Sort</div>
        <div className={styles.subtitle}>Alphabetical</div>
        <div className={styles.sortOptionButtons}>
          {renderSortButton(
            SortOptions.NAME,
            'Ascending (A-Z)',
            KeyboardArrowDownIcon,
            SortType.ASC,
          )}
          {renderSortButton(
            SortOptions.NAME,
            'Descending (Z-A)',
            KeyboardArrowUpIcon,
            SortType.DESC,
          )}
        </div>

        <div className={styles.subtitle}>By date</div>
        <div className={styles.sortOptionButtons}>
          {renderSortButton(
            SortOptions.LAST_VIEWED,
            'Last Viewed',
            PlayIcon,
          )}
          {renderSortButton(
            SortOptions.UPDATED_AT,
            'Last Modified',
            EditIcon,
          )}
        </div>

        <div className={styles.sortOptionButtons}>
          {renderSortButton(SortOptions.CREATED_AT, 'Created', EditIcon)}
        </div>

        <div className={styles.subtitle}>Other</div>
        <div className={styles.sortOptionButtons}>
          {renderSortButton(SortOptions.SIZE, 'Size', StorageIcon)}
          {canSortByOwner &&
            renderSortButton(
              SortOptions.OWNER,
              'Owner',
              AccountBoxIcon,
              undefined,
              SortType.ASC,
            )}
        </div>

        <div className={styles.modalSubtitle}>Filter</div>
        {selectedFilters.length > 0 && (
          <div className={styles.selectedFilters}>
            {selectedFilters.map((filter) => (
              <OutlinedButton
                key={`${filter.value}-${filter.label}`}
                kind="purple"
                className={styles.chip}
              >
                <div>{filter.label}</div>
                <CheckIcon sx={{ height: '16px' }} />
              </OutlinedButton>
            ))}
          </div>
        )}

        <div className={styles.filterOptionSelectors}>
          <div className={styles.selectorContainer}>
            <div className={styles.subtitle}>Status</div>
            <PermissionSelector
              title="Status"
              selectedValues={[
                STATUS_OPTIONS.find((option) => option.value === status) ||
                  STATUS_OPTIONS[0],
              ]}
              options={STATUS_OPTIONS}
              className={localStyles.selector}
              onSelect={(values) =>
                onStatusChange(values[0].value as VIDEOFEED_STATUS_FILTER)
              }
              singleValueSelector
              withoutCheckBox
              style={{ left: '0px' }}
            />
          </div>
          <div className={styles.selectorContainer}>
            <div className={styles.subtitle}>File Type</div>
            <PermissionSelector
              title="File Type"
              selectedValues={[
                FILE_TYPE_OPTIONS.find(
                  (option) => option.value === contentFilter,
                ) || FILE_TYPE_OPTIONS[0],
              ]}
              options={FILE_TYPE_OPTIONS}
              className={localStyles.selector}
              onSelect={(values) =>
                onContentFilterChange(
                  values[0].value as FOLDER_CONTENT_FILTER,
                )
              }
              singleValueSelector
              withoutCheckBox
            />
          </div>
        </div>

        {canSortByOwner && (
          <div className={styles.filterOptionSelectors}>
            <div className={styles.selectorContainer}>
              <div className={styles.subtitle}>Owner</div>
              <PermissionSelector
                title="Owner"
                selectedValues={selectedOwners}
                options={ownerOptions}
                className={localStyles.selector}
                onSelect={onOwnerChange}
                searchable
                style={{ left: '0px' }}
              />
            </div>
          </div>
        )}

        <ContainedButton
          style={{ width: '100%', marginTop: '20px' }}
          onClick={onClose}
        >
          Close
        </ContainedButton>
      </div>
    </Modal>
  );
};

export default VideofeedFiltersModal;
